import printJS from "print-js"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useQuery } from "react-query"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import TrackingLink from "src/components/TrackingLink"

import { homeportPrompt } from "src/api/Contracts/SignContract"

import { useTracker } from "src/hooks/use_tracker"

import AssignHomeportModal from "../AssignHomeportModal"

const AccountSubmitted = ({
  boat,
  contractChatUrl,
  copy,
  downloadPdfUrl,
  homeport,
  homeportIsMarina,
  marina,
  mobileWebview,
  quoteCompleted,
  quoteId,
  reservationUrl,
  savedPdfUrl,
}) => {
  const tracker = useTracker()
  const [completed, setCompleted] = useState(quoteCompleted)
  const [showSuccessBanner, setShowSuccessBanner] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data } = useQuery(
    ["homeportPrompt", quoteId],
    () => homeportPrompt({ quoteId }),
    {
      initialData: { quoteCompleted, reservationUrl },
      refetchInterval: 2000,
      enabled: !completed,
    }
  )

  useEffect(() => {
    if (data.quoteCompleted) {
      setCompleted(true)
    }
  }, [data])

  const trackAgreement = (eventName) => {
    tracker.trackEvent(eventName, {
      current_step: "submitted",
      contract_quote_id: quoteId,
      marina_id: marina.id,
      marina_name: marina.name,
    })
  }

  const handleView = () => trackAgreement("contracts_v2:agreement_viewed")
  const handleDownload = () =>
    trackAgreement("contracts_v2:agreement_downloaded")

  const handlePrint = (event) => {
    event.preventDefault()

    trackAgreement("contracts_v2:agreement_printed")

    printJS({
      printable: savedPdfUrl,
      type: "pdf",
    })
  }

  const trackingProps = {
    boat_id: boat?.encoded_id,
    marina_id: marina.id,
  }

  const renderViewButton = () => {
    if (data.quoteCompleted && data.reservationUrl) {
      return (
        <a
          className="btn btn-primary w-full"
          href={data.reservationUrl}
          onClick={handleView}
        >
          {copy.view}
        </a>
      )
    }

    return (
      <Button variant="primary" fullWidth disabled>
        {copy.processing}
      </Button>
    )
  }

  const renderHomeportPanel = () => (
    <>
      <div className="pb-6">
        <img
          src={marina.image}
          alt={marina.name}
          className="h-32 w-full rounded object-cover"
        />
      </div>
      <div className="flex flex-col items-center pb-6 lg:items-start">
        <h4 className="text-xl font-bold">{copy.homeport.title}</h4>
        <span className="text-gray-700">{copy.homeport.body}</span>
        <div className="mt-6">
          {showSuccessBanner ? (
            <AlertBanner type="success">{copy.homeport.success}</AlertBanner>
          ) : (
            <Button onClick={() => setIsModalOpen(true)} variant="primary">
              {copy.homeport.cta}
            </Button>
          )}
        </div>
      </div>
      <div className="my-9 w-full border-b border-gray-300" />
    </>
  )

  const exploreCards = [
    {
      key: "trips",
      icon: "fa-solid fa-calendar-days",
      href: "/account/trips",
      eventName: "contracts_v2:view_trips_pressed",
    },
    {
      key: "marinas",
      icon: "fa-solid fa-magnifying-glass",
      href: "/search",
      eventName: "contracts_v2:explore_marinas_pressed",
    },
    boat && {
      key: "boat",
      icon: "fa-solid fa-ship",
      href: `/account/boats/${boat.encoded_id}/edit`,
      eventName: "contracts_v2:update_boater_info_pressed",
    },
    {
      key: "payments",
      icon: "fa-solid fa-credit-card",
      href: "/account/settings/payment_methods",
      eventName: "contracts_v2:update_payment_info_pressed",
    },
  ].filter(Boolean)

  const renderOtherBenefits = () => (
    <>
      <h4 className="text-lg font-semibold text-gray-900">
        {copy.explore.title}
      </h4>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {exploreCards.map((card) => (
          <div
            key={card.key}
            className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3"
          >
            <i className={`${card.icon} fa-fw fa-lg text-gray-900`} />
            <span className="text-gray-700">{copy.explore[card.key].copy}</span>
            <div className="col-start-2">
              <TrackingLink
                className="btn btn-tertiary"
                href={card.href}
                eventName={card.eventName}
                eventProperties={trackingProps}
              >
                {copy.explore[card.key].label}
              </TrackingLink>
            </div>
          </div>
        ))}
      </div>
    </>
  )

  return (
    <div className="grid w-full grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-5">
        <div className="rounded-lg border border-gray-300 bg-white p-6">
          <h2 className="m-0 text-2xl font-bold text-gray-900">
            {copy.savedTitle}
          </h2>
          <p className="mb-6 mt-2 text-gray-700">{copy.savedBody}</p>
          <div className="flex flex-col gap-3">
            {renderViewButton()}
            <a
              className="btn btn-secondary w-full"
              href={downloadPdfUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={handleDownload}
            >
              {copy.download}
            </a>
            {!mobileWebview && (
              <Button variant="secondary" fullWidth onClick={handlePrint}>
                {copy.print}
              </Button>
            )}
          </div>
          <div className="mt-6 text-center">
            <p className="mb-2 font-semibold text-gray-900">{copy.question}</p>
            <TrackingLink
              className="btn btn-tertiary w-full"
              href={contractChatUrl}
              newTab={true}
              eventName="contracts_v2:marina_messaged"
              eventProperties={{
                contract_quote_id: quoteId,
                marina_id: marina.id,
                marina_name: marina.name,
              }}
            >
              {copy.message}
            </TrackingLink>
          </div>
        </div>
      </div>
      <div className="col-span-12 flex flex-col lg:col-span-7">
        {boat && !homeportIsMarina && renderHomeportPanel()}
        {renderOtherBenefits()}
      </div>
      {boat && (
        <AssignHomeportModal
          boat={boat}
          homeport={homeport}
          isOpen={isModalOpen}
          marina={marina}
          quoteId={quoteId}
          setIsOpen={setIsModalOpen}
          setShowSuccessBanner={setShowSuccessBanner}
        />
      )}
    </div>
  )
}

const exploreCardPropType = PropTypes.shape({
  copy: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
})

AccountSubmitted.propTypes = {
  boat: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    encoded_id: PropTypes.string,
    name: PropTypes.string,
  }),
  contractChatUrl: PropTypes.string.isRequired,
  copy: PropTypes.shape({
    savedTitle: PropTypes.string.isRequired,
    savedBody: PropTypes.string.isRequired,
    view: PropTypes.string.isRequired,
    processing: PropTypes.string.isRequired,
    download: PropTypes.string.isRequired,
    print: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    homeport: PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      cta: PropTypes.string.isRequired,
      success: PropTypes.string.isRequired,
    }),
    explore: PropTypes.shape({
      title: PropTypes.string.isRequired,
      trips: exploreCardPropType.isRequired,
      marinas: exploreCardPropType.isRequired,
      boat: exploreCardPropType.isRequired,
      payments: exploreCardPropType.isRequired,
    }).isRequired,
  }).isRequired,
  downloadPdfUrl: PropTypes.string.isRequired,
  homeport: PropTypes.object,
  homeportIsMarina: PropTypes.bool.isRequired,
  marina: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image: PropTypes.string,
  }).isRequired,
  mobileWebview: PropTypes.bool.isRequired,
  quoteCompleted: PropTypes.bool.isRequired,
  quoteId: PropTypes.string.isRequired,
  reservationUrl: PropTypes.string,
  savedPdfUrl: PropTypes.string.isRequired,
}

export default AccountSubmitted
